import { cn } from '#/lib/utils';
import {
  rememberAsyncFilterLabels,
  resolveFromLabelCache,
} from '#/modules/commonComponents/table/asyncFilterLabelCache';
import {
  createAsyncMultiFilterMeta,
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import {
  StocktakeComparisonAnomalyBadges,
  StocktakeComparisonIdsExpand,
} from '#/modules/imsMgmt/receipts/stocktake/components/StocktakeComparisonCellParts';
import {
  isComparisonRowAbnormal,
  resolveComparisonStockNo,
  resolveWarehouseLabel,
  STOCKTAKE_ABNORMAL_FILTER_OPTIONS,
  STOCKTAKE_ANOMALY_FILTER_OPTIONS,
} from '#/modules/imsMgmt/receipts/stocktake/helper/comparisonDisplay';
import {
  resolveLineMaterial,
  resolveLineMaterialNo,
} from '#/modules/imsMgmt/receipts/stocktake/helper/resolveLineMaterial';
import { searchWarehouseFilterOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import { formatDisplayQty } from '#/utils/formatNumber';

/** 比對列表欄：不顯示／不比對 Location。 */
export function buildStocktakeComparisonColumns({
  filters = {},
  onFilterChange,
  warehouseLabelCache,
} = {}) {
  return [
    {
      id: 'abnormal',
      header: '異常',
      size: 88,
      enableSorting: true,
      enableColumnFilter: true,
      meta: createFixedMultiFilterMeta({
        key: 'abnormal',
        label: '異常',
        options: STOCKTAKE_ABNORMAL_FILTER_OPTIONS,
        filterValue: filters.abnormal,
        onChange: (value) => onFilterChange('abnormal', value),
      }),
      cell: ({ row }) => {
        const abnormal = isComparisonRowAbnormal(row.original);
        return (
          <span
            className={cn(
              'text-sm font-medium',
              abnormal ? 'text-amber-700' : 'text-slate-400',
            )}
          >
            {abnormal ? '是' : '否'}
          </span>
        );
      },
    },
    {
      id: 'stockNo',
      header: '庫存編號',
      size: 130,
      enableSorting: true,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'stockNo',
        label: '庫存編號',
        filterValue: filters.stockNo,
        onChange: (value) => onFilterChange('stockNo', value),
        placeholder: '搜尋庫存編號',
      }),
      cell: ({ row }) => resolveComparisonStockNo(row.original) || '—',
    },
    {
      id: 'materialNo',
      header: '物料',
      size: 160,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'materialNo',
          label: '料號',
          filterValue: filters.materialNo,
          onChange: (value) => onFilterChange('materialNo', value),
          placeholder: '搜尋料號',
        }),
      },
      cell: ({ row }) => {
        const mtr = resolveLineMaterial(row.original);
        if (!mtr) {
          return '—';
        }
        return (
          <MaterialNameStack
            name={mtr.name}
            code={resolveLineMaterialNo(mtr)}
          />
        );
      },
    },
    {
      id: 'countedQty',
      header: '盤點量',
      size: 100,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'text-right',
        ...createTextFilterMeta({
          key: 'countedQty',
          label: '盤點量',
          filterValue: filters.countedQty,
          onChange: (value) => onFilterChange('countedQty', value),
          placeholder: '搜尋盤點量',
        }),
      },
      cell: ({ row }) => formatDisplayQty(
        row.original.countedQty ?? row.original.countedQuantity,
      ),
    },
    {
      id: 'currentQty',
      header: '系統量',
      size: 100,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'text-right',
        ...createTextFilterMeta({
          key: 'currentQty',
          label: '系統量',
          filterValue: filters.currentQty,
          onChange: (value) => onFilterChange('currentQty', value),
          placeholder: '搜尋系統量',
        }),
      },
      cell: ({ row }) => formatDisplayQty(
        row.original.currentQty ?? row.original.currentQuantity,
      ),
    },
    {
      id: 'differenceQty',
      header: '差異',
      size: 96,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'text-right',
        ...createTextFilterMeta({
          key: 'differenceQty',
          label: '差異',
          filterValue: filters.differenceQty,
          onChange: (value) => onFilterChange('differenceQty', value),
          placeholder: '搜尋差異',
        }),
      },
      cell: ({ row }) => {
        const diff = row.original.differenceQty
          ?? row.original.differenceQuantity;
        if (diff == null || diff === '') {
          return '—';
        }
        const numeric = Number(diff);
        const className = Number.isFinite(numeric) && numeric !== 0
          ? 'font-medium text-amber-700'
          : undefined;
        return <span className={className}>{formatDisplayQty(diff)}</span>;
      },
    },
    {
      id: 'countedWarehouse',
      header: '盤點倉',
      size: 140,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createAsyncMultiFilterMeta({
          key: 'countedWarehouseId',
          label: '盤點倉',
          searchOptions: searchWarehouseFilterOptions,
          filterValue: filters.countedWarehouseId,
          onChange: (value) => onFilterChange('countedWarehouseId', value),
          resolveFilterDisplay: (values) => resolveFromLabelCache(
            warehouseLabelCache,
            values,
          ),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(warehouseLabelCache, options, values);
          },
        }),
      },
      cell: ({ row }) => resolveWarehouseLabel(row.original.countedWarehouse),
    },
    {
      id: 'currentWarehouse',
      header: '系統倉',
      size: 140,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createAsyncMultiFilterMeta({
          key: 'currentWarehouseId',
          label: '系統倉',
          searchOptions: searchWarehouseFilterOptions,
          filterValue: filters.currentWarehouseId,
          onChange: (value) => onFilterChange('currentWarehouseId', value),
          resolveFilterDisplay: (values) => resolveFromLabelCache(
            warehouseLabelCache,
            values,
          ),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(warehouseLabelCache, options, values);
          },
        }),
      },
      cell: ({ row }) => resolveWarehouseLabel(row.original.currentWarehouse),
    },
    {
      id: 'anomalies',
      header: '異常類型',
      size: 180,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createFixedMultiFilterMeta({
          key: 'anomalies',
          label: '異常類型',
          options: STOCKTAKE_ANOMALY_FILTER_OPTIONS,
          filterValue: filters.anomalies,
          onChange: (value) => onFilterChange('anomalies', value),
        }),
      },
      cell: ({ row }) => (
        <StocktakeComparisonAnomalyBadges anomalies={row.original.anomalies} />
      ),
    },
    {
      id: 'stocktakeNo',
      header: '盤點單',
      size: 160,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'stocktakeNo',
          label: '盤點單號',
          filterValue: filters.stocktakeNo,
          onChange: (value) => onFilterChange('stocktakeNo', value),
          placeholder: '搜尋盤點單號',
        }),
      },
      cell: ({ row }) => <StocktakeComparisonIdsExpand row={row.original} />,
    },
  ];
}
